import { DateTime, Duration, Interval } from "luxon"
import { IntlShape } from "react-intl"

import { DateString } from "@shared/dtos/responses/commonResponses.dto"
import { getDateStringFromQuery } from "@shared/utils/date"

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

export const MS_MINUTE = 60000
export const MS_HOUR = MS_MINUTE * 60
export const MS_DAY = MS_HOUR * 24
export const MS_YEAR = MS_DAY * 365 // Close enough

export const dateSeparators = ["/", "-", ".", " "]
export const dateStringSplitRegex = /[-/. ]/

// EEEE dd MMMM yyyy
export const formatDate = (dateString: string | Date) => {
  const date = parseDayString(dateString)
  if (!date) {
    return ""
  }

  const day = date.toLocaleString("default", { weekday: "long" })
  const dayNumber = date.getDate()
  const month = date.toLocaleString("default", { month: "long" })
  const year = date.getFullYear()
  return `${day} ${dayNumber} ${month} ${year}`
}

export const formatTime = (fromDateString: string, toDateString: string) => {
  const fromDate = new Date(fromDateString)
  const toDate = new Date(toDateString)
  return `${timeFormat(fromDate)} - ${timeFormat(toDate)}`
}

// Parse a date with format dd-MM-yyyy
export const parseDayString = (dateString?: DateTime | Date | string) => {
  if (!dateString) {
    return undefined
  } else if (dateString instanceof DateTime) {
    return dateString.isValid ? dateString.toJSDate() : undefined
  } else if (isDate(dateString)) {
    return dateString
  }

  // Parse known formats
  const potentialDate = getDateStringFromQuery(dateString)
  if (potentialDate?.isValid) {
    return potentialDate.toJSDate()
  }

  // Try ISO date format parser
  const isoDate = DateTime.fromISO(dateString)
  if (isoDate.isValid) {
    return isoDate.toJSDate()
  }

  // Fallback to original parser
  const [dd, mm, yyyy] = dateString
    .split("-")
    .splice(0, 3)
    .map((part) => parseInt(part, 10))
  return new Date(yyyy, mm - 1, dd)
}

// EEEE dd MMMM yyyy HH:mm
export const formatDateWithTime = (dateString: Date | string) => {
  const date = parseDayString(dateString)
  if (!date) {
    return ""
  }

  return `${formatDate(date)} ${timeFormat(date)}`
}

export const dateToDay = (date: Date) => {
  return DateTime.fromJSDate(date).startOf("day").toJSDate()
}

export const dateGetToday = () => dateToDay(new Date())

export const isSameDay = (date1: Date, date2: Date) => {
  const dt1 = DateTime.fromJSDate(date1)
  const dt2 = DateTime.fromJSDate(date2)
  return dt1.hasSame(dt2, "day")
}

export const dateIsToday = (date: Date) => isSameDay(new Date(), date)

export const dateDifferenceInInterval = (date1: Date, date2: Date, interval: number) =>
  Math.floor((date2.valueOf() - date1.valueOf()) / interval)

export const dateDifferenceInMinutes = (date1: Date, date2: Date) => dateDifferenceInInterval(date1, date2, MS_MINUTE)
export const dateDifferenceInDays = (date1: Date, date2: Date) => dateDifferenceInInterval(date1, date2, MS_DAY)
export const dateDifferenceInYears = (date1: Date, date2: Date) => dateDifferenceInInterval(date1, date2, MS_YEAR)

export const dateAddDays = (date: Date, days: number) => new Date(date.valueOf() + MS_DAY * days)
export const dateSubtractDays = (date: Date, days: number) => new Date(date.valueOf() - MS_DAY * days)

// yyyy-MM-dd
export const dateFormatBackend = (date: Date | undefined | null) =>
  date
    ? `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date
        .getDate()
        .toString()
        .padStart(2, "0")}`
    : ""

// dd-MM-yyyy (at start of day)
export const dateFormatStandard = (date: Date | DateTime) => {
  if (date instanceof DateTime) {
    return date.toFormat("dd-MM-yyyy")
  }

  const dateTime = DateTime.fromJSDate(date)
  return dateTime.toFormat("dd-MM-yyyy")
}

//from MMMM yyyy to Date object
export const parseFormatMonth = (dateString: string): Date => {
  const parts = dateString.split(" ")
  const monthName = parts[0]
  const year = parseInt(parts[1], 10)
  const monthIndex = monthNames.indexOf(monthName)
  return new Date(year, monthIndex)
}

//parse date from dd-MM-yyyy to Date object
export const parseFormatStandard = (dateString: string) => {
  const [dd, mm, yyyy] = dateString
    .split(dateStringSplitRegex)
    .splice(0, 3)
    .map((part) => parseInt(part, 10))
  return new Date(yyyy, mm - 1, dd)
}

// MMMM yyyy
export const dateFormatMonth = (date: Date) => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
  })

  return formatter.format(date)
}

// add one month from given date object and return new date object
export const addMonth = (date: Date, operant: "add" | "subtract" = "add") => {
  const newDate = new Date(date)
  if (operant === "subtract") {
    newDate.setMonth(newDate.getMonth() - 1)
  } else {
    newDate.setMonth(newDate.getMonth() + 1)
  }
  return newDate
}

// HH:mm
export const timeFormat = (date: Date) => {
  const hours = date.getHours()
  const minutes = date.getMinutes()
  return `${hours < 10 ? `0${hours}` : hours}:${minutes < 10 ? `0${minutes}` : minutes}`
}

//d
export const dayFormat = (date: Date) => {
  const day = date.getDate()
  return `${day < 10 ? `0${day}` : day}`
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export const isDate = (argument: any): argument is Date => argument instanceof Date && !isNaN(argument.getTime())

//start of the week
export const getFirstDateOfWeek = (date: Date, firstDayIsSunday = false): Date => {
  let firstDayOfWeek = date.getDay()

  if (!firstDayIsSunday) {
    // If Monday is considered the first day of the week
    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1
  }

  const startDate = new Date(date)
  startDate.setDate(date.getDate() - firstDayOfWeek)
  startDate.setHours(0, 0, 0, 0) // set time to midnight

  return startDate
}

//end of the week
export const getLastDateOfWeek = (date: Date, firstDayIsSunday = false): Date => {
  let lastDayOfWeek = date.getDay()

  if (!firstDayIsSunday) {
    // If Monday is considered the first day of the week
    lastDayOfWeek = lastDayOfWeek === 0 ? 6 : lastDayOfWeek - 1
  }

  const endDate = new Date(date)
  endDate.setDate(date.getDate() + (6 - lastDayOfWeek))
  endDate.setHours(23, 59, 59, 999) // set time to midnight

  return endDate
}

//start of the month
export const getFirstDateOfMonth = (date: Date): Date => {
  const startDate = new Date(date)
  startDate.setDate(1)
  startDate.setHours(0, 0, 0, 0) // set time to midnight

  return startDate
}

//end of the month
export const getLastDateOfMonth = (date: Date): Date => {
  const endDate = new Date(date)
  endDate.setMonth(date.getMonth() + 1)
  endDate.setDate(0)
  endDate.setHours(23, 59, 59, 999) // set time to midnight

  return endDate
}

//add one day
export const addDay = (date: Date, numberOfDays = 1): Date => {
  const newDate = new Date(date)
  newDate.setDate(newDate.getDate() + numberOfDays)
  return newDate
}

//each day of interval
export const getDatesOfInterval = ({ startDate, endDate }: { startDate: Date; endDate: Date }): Date[] => {
  const dates = []
  let currentDate = startDate

  while (currentDate <= endDate) {
    dates.push(currentDate)
    currentDate = addDay(currentDate)
  }

  return dates
}

//is same month
export const isSameMonth = (date1: Date, date2: Date) => {
  return date1.getMonth() === date2.getMonth() && date1.getFullYear() === date2.getFullYear()
}

export const isBefore = (date1: Date, date2: Date): boolean => date1.getTime() < date2.getTime()

export const isAfter = (date1: Date, date2: Date): boolean => date1.getTime() > date2.getTime()

export const compareDesc = (date1: Date, date2: Date): boolean => (date2.getTime() - date1.getTime() > 0 ? true : false)

export const dateStringToDateTime = (input: DateString): DateTime => {
  return input instanceof Date ? DateTime.fromJSDate(input) : DateTime.fromISO(input)
}

/**
 * Helper to combine two date objects
 */
export const combineDateAndTime = (date?: Date, time?: Date): DateTime | undefined => {
  const dTime = time ? DateTime.fromJSDate(time) : undefined
  const dDate = date ? DateTime.fromJSDate(date) : undefined

  if (dDate) {
    // Default to end of day
    return dDate.set({
      hour: dTime?.hour ?? 23,
      minute: dTime?.minute ?? 59,
      second: 0,
      millisecond: 0,
    })
  }

  return undefined
}

export const localizedDuration = (
  start: DateTime | undefined,
  end: DateTime | undefined,
  intl: IntlShape
): string | undefined => {
  if (start && end) {
    const interval = Interval.fromDateTimes(start, end)
    const diff = interval.toDuration(["hours", "minutes", "seconds"])

    return formatDuration(diff, intl)
  }

  return undefined
}

/** Returns localized duration string (using `intl` with fallback to luxon) */
const formatDuration = (duration: Duration, intl?: IntlShape): string => {
  if (!intl) {
    return duration.toHuman()
  }

  const parts = []
  const values = duration.toObject()

  if (values.hours) {
    parts.push(intl.formatMessage({ id: "duration.hours" }, { count: Math.round(values.hours) }))
  }

  if (values.minutes) {
    parts.push(intl.formatMessage({ id: "duration.minutes" }, { count: Math.round(values.minutes) }))
  }

  if (values.seconds) {
    parts.push(intl.formatMessage({ id: "duration.seconds" }, { count: Math.round(values.seconds) }))
  }

  return parts.join(", ")
}

export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))
